import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Upload, ClipboardPaste, X, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-07 — stage 2 of the meeting page: what the person gives us about the call.
 *
 * The same intake as the Slack ask: a pasted note and an uploaded file land in
 * one place, and the same content twice is stored once. The raw text is never
 * shown back here (only that it exists, where it came from and how long it is) —
 * it is kept under the retention policy for the next steps to read.
 */

interface CaptureRow {
  id: number;
  source: string;
  filename: string | null;
  char_count: number;
  created_at: string;
  expires_at: string | null;
}
interface CaptureStatus {
  stage: 'done' | 'waiting' | 'skipped';
  ask: { status: string; asked_at: string | null; closed_reason: string | null; skip_reason: string | null } | null;
  captures: CaptureRow[];
}

const SOURCE_LABEL: Record<string, string> = {
  paste: 'Pasted notes',
  upload: 'Uploaded file',
  voice: 'Voice debrief',
  teams: 'Teams transcript',
  slack_file: 'Slack file',
};

// What the Slack ask is doing for this meeting, in the person's words.
const ASK_NOTE: Record<string, string> = {
  queued: 'Jarvis will ask you in Slack a few minutes after the call.',
  sending: 'Jarvis is asking you in Slack now.',
  sent: 'Jarvis asked you in Slack. You can answer there or here.',
  reminded: 'Jarvis asked you in Slack and sent one reminder. You can answer there or here.',
};

const ACCEPT = '.txt,.md,.vtt,.srt,.docx,.pdf';

const errorOf = (err: any, fallback: string): string => err?.response?.data?.error || fallback;

const MeetingCapturePanel: React.FC<{ meetingId: number; onChanged?: () => void }> = ({ meetingId, onChanged }) => {
  const [status, setStatus] = useState<CaptureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/bd/captures/meeting/${meetingId}`);
      setStatus({ stage: res.data?.stage, ask: res.data?.ask || null, captures: res.data?.captures || [] });
      setFailed(false);
    } catch (err) {
      setStatus(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const done = async (message: string) => {
    toast.success(message);
    await load();
    if (onChanged) onChanged();
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(errorOf(err, 'That did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const savePaste = () => run(async () => {
    const res = await api.post(`/api/bd/captures/meeting/${meetingId}/paste`, { text });
    setText('');
    setShowPaste(false);
    await done(res.data?.duplicate ? 'You already had exactly that logged.' : 'Notes logged.');
  });

  const uploadFile = (file: File | undefined) => {
    if (!file) return;
    run(async () => {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`/api/bd/captures/meeting/${meetingId}/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await done(res.data?.duplicate ? 'You already had exactly that file logged.' : 'File logged.');
    }).finally(() => { if (fileRef.current) fileRef.current.value = ''; });
  };

  const noNotes = () => run(async () => {
    await api.post(`/api/bd/captures/meeting/${meetingId}/no-notes`);
    await done('Marked as no notes.');
  });

  const notBd = () => run(async () => {
    await api.post(`/api/bd/captures/meeting/${meetingId}/not-bd`);
    await done('Marked as not a BD meeting.');
  });

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-400 mb-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }
  if (failed || !status) {
    return <p className="text-sm text-red-600 mb-6" data-testid="bd-capture-error">The capture details could not be loaded.</p>;
  }

  const captured = status.captures.length > 0;
  const askNote = status.ask && ASK_NOTE[status.ask.status];

  return (
    <div className="mb-6 space-y-4" data-testid="bd-capture-panel">
      {askNote && !captured && status.stage === 'waiting' && (
        <p className="text-xs text-gray-500" data-testid="bd-capture-ask-note">{askNote}</p>
      )}

      {captured && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-testid="bd-capture-list">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Logged for this call</h4>
          <ul className="space-y-1">
            {status.captures.map((c) => (
              <li key={c.id} className="text-sm text-gray-700">
                {SOURCE_LABEL[c.source] || c.source}
                {c.filename ? ` — ${c.filename}` : ''}
                <span className="text-xs text-gray-400"> · {c.char_count.toLocaleString()} characters · {new Date(c.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
          {status.captures.some((c) => c.expires_at) && (
            <p className="text-xs text-gray-400 mt-2">The raw text is deleted on the retention schedule; the summary is kept.</p>
          )}
        </div>
      )}

      {status.stage === 'skipped' && !captured && (
        <p className="text-sm text-gray-500" data-testid="bd-capture-skipped">
          {status.ask?.closed_reason === 'no_reply'
            ? 'No notes were logged for this call.'
            : 'You marked this call as having no notes. You can still add some below.'}
        </p>
      )}

      <div className="border border-dashed border-gray-200 rounded-lg p-4">
        <p className="text-sm font-medium text-slate-700">{captured ? 'Add more' : 'Log this call'}</p>
        <p className="text-xs text-gray-500 mt-1 mb-3">
          Upload a transcript (.txt, .vtt, .srt, .docx or .pdf) or paste your notes. The same thing twice is only logged once.
        </p>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="bd-capture-file" onChange={(e) => uploadFile(e.target.files?.[0])} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" /> Upload transcript
          </button>
          <button
            onClick={() => setShowPaste((v) => !v)}
            disabled={busy}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            data-testid="bd-capture-paste-toggle"
          >
            <ClipboardPaste className="w-3.5 h-3.5" /> Paste notes
          </button>
          {!captured && (
            <button
              onClick={noNotes}
              disabled={busy}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              data-testid="bd-capture-no-notes"
            >
              <X className="w-3.5 h-3.5" /> No notes this time
            </button>
          )}
          <button
            onClick={notBd}
            disabled={busy}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            data-testid="bd-capture-not-bd"
            title="Tell Jarvis this is not a business-development meeting, so it stops asking about meetings like it"
          >
            <Ban className="w-3.5 h-3.5" /> Not a BD meeting
          </button>
        </div>

        {showPaste && (
          <div className="mt-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder="What was said, what was agreed, what happens next…"
              className="w-full border border-gray-300 rounded p-2 text-sm"
              data-testid="bd-capture-text"
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={savePaste}
                disabled={busy || text.trim().length < 20}
                className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                data-testid="bd-capture-save"
              >
                Log notes
              </button>
              <span className="text-xs text-gray-400">{text.trim().length < 20 ? 'A few sentences at least.' : `${text.trim().length.toLocaleString()} characters`}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingCapturePanel;
